import { useTranslation } from 'react-i18next';
import { Reservation, ReservationPriceModifier } from '@turizi/shared';
import { formatPrice, formatExchangeRate, formatDayLongTime } from '@/lib/format-utils';
import { Badge } from '@mantine/core';

interface Props {
    reservation: Reservation;
    className?: string;
}

export default function ReservationPriceModifiersView({ reservation, className = '' }: Props) {
    const { t } = useTranslation();
    const modifiers: ReservationPriceModifier[] = reservation?.price_modifiers || [];

    if (modifiers.length === 0) {
        return null;
    }

    return (
        <div className={`space-y-2 ${className}`}>
            {modifiers.map((mod) => {
                const val = Number(mod.value || 0);
                const formattedSign = val > 0 ? '+' : (val < 0 ? '-' : '');
                const absVal = Math.abs(val);
                const customDesc = mod.description && mod.description.trim() !== t(mod.section) && mod.description.trim() !== t('currency_exchange');

                if (mod.type === 'exchange' || mod.section === 'exchange') {
                    const sourceCurr = mod.description ? mod.description.trim().split(/[\s→:]+/)[0]?.toUpperCase() : null;
                    const targetCurr = mod.currency;

                    let origAmount = 0;
                    if (sourceCurr && reservation?.service_bookings) {
                        reservation.service_bookings.forEach(b => {
                            if (b.money_in_currency === sourceCurr && b.money_in_amount !== null) {
                                origAmount += Number(b.money_in_amount || 0);
                            }
                        });
                    }

                    const dateStr = mod.updated_at || mod.created_at;
                    const formattedDate = dateStr ? formatDayLongTime(dateStr) : null;

                    return (
                        <div
                            key={mod.id}
                            className="p-3 bg-slate-50/80 rounded-xl space-y-2 text-xs"
                        >
                            {/* Row 1: Badge & Date */}
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                    <Badge
                                        size="xs"
                                        variant="light"
                                        color="cyan"
                                        className="shrink-0 text-[10px] font-bold uppercase tracking-wider"
                                    >
                                        {t('currency_exchange')}
                                    </Badge>
                                    {formattedDate && (
                                        <span className="text-[11px] text-slate-400 font-medium whitespace-nowrap">
                                            {formattedDate}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Row 2: Currency Pair & Rate Badge */}
                            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                                <span className="font-bold text-xs text-slate-800 tracking-tight">
                                    {mod.description || `${sourceCurr} → ${targetCurr}`}
                                </span>
                                <span className="text-xs font-semibold text-slate-700 bg-white/90 px-2 py-0.5 rounded-md">
                                    {t('exchange_rate')}: <span className="font-bold text-blue-700">{formatExchangeRate(val)}</span>
                                </span>
                            </div>

                            {/* Row 3: Original Base Amount */}
                            <div className="pt-1 flex items-center justify-between text-xs flex-wrap gap-1">
                                <span className="text-slate-400 text-[11px] font-medium uppercase tracking-wider">
                                    {t('original_amount')}:
                                </span>
                                <span className="font-bold text-slate-700">
                                    ${formatPrice(origAmount)} {sourceCurr}
                                </span>
                            </div>
                        </div>
                    );
                }

                return (
                    <div
                        key={mod.id}
                        className="flex items-center justify-end gap-2 text-xs min-w-0"
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            <Badge
                                size="xs"
                                variant="light"
                                color="black"
                                className="shrink-0 text-[10px] font-semibold uppercase tracking-wider"
                            >
                                {mod.section === 'exchange' ? t('currency_exchange') : t(mod.section)}
                            </Badge>
                            {customDesc && (
                                <span className="font-medium text-slate-700 whitespace-nowrap text-xs" title={mod.description || ''}>
                                    {mod.description}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <span className="font-bold text-xs">
                                {formattedSign}{mod.type === 'percentage' ? '%' : '$'} {formatPrice(absVal)} {mod.currency || ''}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
